import { Component, inject, Injector, OnDestroy, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AutoReplyTemplatesService } from '@modules/account/services/auto-reply-templates.service';
import { StitchServiceFactory } from '@modules/common/factories/stitch-service.factory';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Application } from '@modules/common/types/application';
import { isKnownTab, Tab } from '@modules/common/types/tab';
import { MailAppStateService } from '@modules/messages/services/mail-app-state.service';
import { MessageFolderListStateService } from '@modules/messages/services/message-folder-list-state.service';
import { MessageDetailsCache } from '@modules/messages/services/message-details.cache';
import { MessageListStateService } from '@modules/messages/services/message-list-state.service';
import { MailReaderOpenService } from '@modules/messages/services/mail-reader-open.service';
import { MessagesService } from '@modules/messages/services/messages.service';
import { ListState } from '@modules/messages/types/list-state';
import { Message } from '@modules/messages/types/message';
import { ProductAnalyticsService } from '@frontendlib/common/services/product-analytics/product-analytics.service';
import { MessageFolder } from '@modules/messages/types/message-folder';
import { MessageFoldersFilters } from '@modules/messages/types/message-folders-filters';
import { MessagesFilters } from '@modules/messages/types/messages-filters';
import { MessagesListState } from '@modules/messages/types/messages-list-state';
import { VirtualFolder } from '@modules/messages/types/virtual-folder';
import { ModalService } from '@modules/modal/services/modal.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { MessageFolderSummaryDto } from '@sharedlib/dtos/dto/mail-app/folder.dto';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { MessageSummaryDto } from '@sharedlib/dtos/dto/mail-app/message.dto';
import { combineLatest, EMPTY, Subject } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-messages2-list-container',
  templateUrl: './list-container.component.html',
  styleUrls: ['./list-container.component.less'],
  standalone: false,
  providers: [
    ...MessageListStateService.providers({
      stateKey: StateKey.messagesListState,
      withAdvancedSearch: true,
    }),
    ...MessageFolderListStateService.providers({
      stateKey: StateKey.messagesFoldersListState,
      withAdvancedSearch: true,
    }),
  ],
})
export class MessagesListContainerComponent implements OnInit, OnDestroy {
  public minimized: boolean;
  public listState: ListState;
  public sidebarFilter: VirtualFolder = 'inbox';
  public quickFolder: MessageFolder;
  public quickMessage: Message;
  public loading: boolean;
  public tabs: Tab<Exclude<ListState, 'tabs'>>[] = [
    { title: 'Folders', value: 'folders' },
    { title: 'Messages', value: 'messages' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'messages';
  public foldersFilters: MessageFoldersFilters;
  public messagesFilters: MessagesFilters;
  public debug: 'score' = null;
  public messageListState: MessagesListState;
  public autoReplyVisible = false;
  public containerName: string | null = null;

  private readonly messageListStateService = inject(MessageListStateService, { self: true });
  private readonly messageFolderListStateService = inject(MessageFolderListStateService, { self: true });
  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly stitchServiceFactory = inject(StitchServiceFactory);
  private readonly messageDetailsCache = inject(MessageDetailsCache);

  private readonly alive = new Subject<void>();

  /**
   * Opening the main view is selection-driven, so walking the list with the arrow keys queues
   * one open per row you land on. `switchMap` drops the fetch still in flight: it saves the
   * requests, and it stops an earlier response from arriving late and painting the wrong item.
   * Messages and folders share the one stream because they share the one pane: a folder opened
   * while a message fetch is in flight must drop that fetch the same way a second message does.
   *
   * Each fetch is a one-shot read of `getItem`, which is bound to the service's refresh stream and
   * would otherwise re-emit — and repaint the pane — on every `forceRefresh()` for as long as this
   * container lives. And a fetch that fails once its retries are spent (the service toasts and
   * rethrows) is contained inside the switchMap, or the error would end this subject's subscription
   * and every later open would go silently dead.
   *
   * A message is read through {@link MessageDetailsCache}, so reopening one makes no request (SBUX-1091). Its
   * read is not cancelled by moving on: it finishes into the cache, and the switchMap still keeps it off the pane.
   */
  /** Every open of the reader, the list's own and the search popup's "Open email" alike (searchopen r2). */
  private readonly readerOpen = inject(MailReaderOpenService);
  private readonly productAnalytics = inject(ProductAnalyticsService);

  private readonly messagesService = inject(MessagesService);
  private readonly autoReplyTemplatesService = inject(AutoReplyTemplatesService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  protected readonly isAdvancedSearchApplied: Signal<boolean> = toSignal(
    this.advancedSearchService.isAdvancedSearchApplied(Application.mail),
    { initialValue: false },
  );

  constructor(
    private splitViewService: SplitViewService,
    private stateService: MailAppStateService,
    private modalService: ModalService,
    private searchService: AdvancedSearchService,
    private injector: Injector,
  ) {}

  /**
   * Lifecycle
   */

  /**
   * `stateService.getTeam()` sits in BOTH `combineLatest` below for its IDENTITY effect, and the
   * mailbox tenant never becomes a query param: it reaches the wire as `space_id`, stamped by
   * `createSpacesInterceptor` from `MailboxTenantScopeService`.
   *
   * It is load-bearing twice over. A mailbox change rebuilds the filters object, and
   * `BaseStitchListComponent.ngOnChanges` refetches on a `filters` REFERENCE change — without it the
   * switch is invisible to the list and every tenant change has to be paired by hand with a
   * `MessagesService.forceRefresh()`, which repaints AFTER a request has already gone out under the
   * previous mailbox. And because `getTeam()` is now gated, subscribing to it is what holds the first
   * read until the mailbox identity is known (#1049).
   *
   * Do not "simplify" the unused fifth destructure away.
   */
  ngOnInit() {
    this.readerOpen.requests$
      .pipe(
        switchMap(({ item, config, origin }) => {
          if (item.itemType !== StitchType.MESSAGE) {
            // A folder supersedes an email still being read: that email's skeleton goes, or it hides the folder.
            this.stateService.setPendingMessage(null);
            return this.stitchServiceFactory.getStitchItemById(item.itemType, item.id).pipe(
              take(1),
              map((fullItem) => ({ fullItem, origin })),
              catchError(() => EMPTY),
            );
          }
          // A thread reply or a keyboard open had no click to announce it: it shows as pending from here.
          if (this.stateService.pendingMessage()?.id !== item.id) {
            this.stateService.setPendingMessage(item as MessageSummaryDto);
          }
          // `config` names the mailbox a search suggestion came from; a list row is in the mailbox on screen.
          return this.messageDetailsCache.get(item.id, config).pipe(
            take(1),
            // Anything else put in the reader first — a folder from the rail's tree puts itself there directly, the
            // folder already on screen included — wins: the read is dropped and its skeleton cleared, so its late
            // answer cannot replace that choice.
            takeUntil(
              this.stateService.mainViewRequests$.pipe(tap(() => this.stateService.settlePendingMessage(item.id))),
            ),
            map((fullItem) => ({ fullItem, origin })),
            catchError(() => {
              this.stateService.settlePendingMessage(item.id);
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.alive),
      )
      .subscribe(({ fullItem, origin }) => {
        this.stateService.setMainView(fullItem);
        this.stateService.settlePendingMessage(fullItem.id);
        this.readerOpen.markOpened(fullItem.id, origin);
        // Read before the reader marks it read, so `unread` is still what the person saw.
        if (fullItem instanceof Message) {
          const folder = this.stateService.getVirtualFolderSync();
          this.productAnalytics.track('mail_opened', {
            // A row of the search results is opened from search too, not only a suggestion (SBUX-1213).
            origin: folder === this.stateService.searchResultsFolder ? 'search' : origin,
            was_unread: !!fullItem.unread,
            folder: folder ?? 'user_folder',
            has_attachments: fullItem.attachmentsCount > 0,
          });
        }
      });

    combineLatest([
      this.messageFolderListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.stateService.getTeam(),
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      // The 5th element is intentionally not destructured: `getTeam()` is in both tuples for its
      // identity effect only, and mail carries no `selectedTenantId` field of its own.
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState/applyVirtualFolder read it to reckon their day
        // windows in the account zone (SBUX-829).
        this.foldersFilters = new MessageFoldersFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    combineLatest([
      this.messageListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.stateService.getTeam(),
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        this.messageListState = list;
        this.messagesFilters = new MessagesFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    this.splitViewService
      .getMinimized('mailsList')
      .pipe(takeUntil(this.alive))
      .subscribe((minimized: boolean) => {
        this.minimized = minimized;
      });

    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
        // Unconditional, not just on the non-'tabs' branch above: a container recreated by
        // stchSpaceRerender (e.g. on a space switch) re-inits `selectedTab` to its field default, but
        // the app-state service instance survives the switch and keeps whatever tab was last published
        // — republishing here on every emission, including while folder-mapped to 'tabs', overwrites
        // that stale value as soon as this container exists instead of only once the folder next
        // changes to a concrete one.
        this.stateService.setSelectedTab(this.selectedTab);
      });

    combineLatest([this.stateService.getVirtualFolder(), this.searchService.getState()])
      .pipe(takeUntil(this.alive))
      .subscribe(([folder, state]) => {
        this.sidebarFilter = folder;
        this.containerName = state[Application.mail]?.containerName ?? null;

        this.quickFolder = MessageFolder.fromAdvancedState(state, folder);
        this.quickMessage = Message.fromAdvancedState(state, folder);
      });

    this.autoReplyTemplatesService
      .read()
      .pipe(takeUntil(this.alive))
      .subscribe((template) => {
        this.autoReplyVisible = !!template?.enabled;
      });
  }

  ngOnDestroy() {
    // An open still waiting out its delay dies with the list; its pending message must not outlive it.
    this.stateService.setPendingMessage(null);
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  protected handleSelectedTabChange(tab: string): void {
    if (!isKnownTab<Exclude<ListState, 'tabs'>>(tab, this.tabs)) {
      return;
    }
    this.selectedTab = tab;
    this.stateService.setSelectedTab(tab);
  }

  handleNewMessage(message: Message) {
    this.messagesService.composeMessage({ message, injector: this.injector });
  }

  handleNewFolder(folder = new MessageFolder()) {
    this.stateService.setMainView(folder);
  }

  handleDoubleClickMessage(message: MessageSummaryDto) {
    this.stitchServiceFactory
      .getStitchItemById(message.itemType, message.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((fullMessage) => {
        this.modalService.openFullForm(fullMessage, this.injector);
      });
  }

  handleDoubleClickFolder(folder: MessageFolderSummaryDto) {
    this.stitchServiceFactory
      .getStitchItemById(folder.itemType, folder.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((fullFolder) => {
        this.modalService.openFullForm(fullFolder, this.injector);
      });
  }

  /** A click names the message before its open (see `MessagesListComponent.handleOpenPending`): read it now. */
  handlePendingMessage(message: MessageSummaryDto | null) {
    this.stateService.setPendingMessage(message);
    if (message) {
      this.messageDetailsCache.prefetch(message.id);
    }
  }

  handleClickMessage(message: MessageSummaryDto) {
    this.readerOpen.open(message);
  }

  handleClickThreadMessage(threadMessage: MessageSummaryDto) {
    this.readerOpen.open(threadMessage);
  }

  handleClickFolder(folder: MessageFolderSummaryDto) {
    this.readerOpen.open(folder);
  }

  doDebug() {
    switch (this.debug) {
      case 'score':
        this.debug = null;
        break;
      default:
        this.debug = 'score';
    }
  }

  /**
   * Persists `enabled = false` on the AutoReply row and triggers a refresh
   * so the shared BehaviorSubject re-emits — the alert and the Auto-Reply
   * Settings page toggle both reflect the change without a navigation.
   */
  handleAutoReplyTurnOff() {
    this.autoReplyTemplatesService
      .setEnabled(false, { emit: true, toast: false })
      .pipe(take(1), takeUntil(this.alive))
      .subscribe(() => {
        this.autoReplyVisible = false;
      });
  }
}
